import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";

import HttpManager from "../../services/HttpManager";
import { push } from "../../navigation/v2/sales/push";
// @ts-ignore: librería copiada en src/vendor (sin tipos)
import qrcode from "../../vendor/qrcode-generator";

/*
	"Usar el celular" (Esteban, 2026-10-19): la asesora está en la PC y toma la foto con su celular. Se enseña un QR con un
	enlace de un solo uso (10 min, sin entrar a WAPP); cuando el celular sube la foto, llega el aviso al instante por
	wapp-push y la foto entra al formulario igual que si se hubiera escogido aquí. Sin aviso (servicio caído) se pregunta
	cada 4 s, solo mientras este recuadro está abierto.
*/

export type TipoCaptura = "id_alumno" | "id_tutor" | "comprobante";

interface Props {
	campusId: number;
	tipo: TipoCaptura;
	onFoto: (archivo: File) => void;
	onClose: () => void;
}

const FotoCelular: React.FC<Props> = ({ campusId, tipo, onFoto, onClose }) => {
	const http = HttpManager.getInstance();
	const [enlace, setEnlace] = useState<{ token: string; url: string; vence: string; titulo: string } | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [bajando, setBajando] = useState<boolean>(false);
	const [ahora, setAhora] = useState<number>(Date.now());
	const listo = useRef<boolean>(false);

	const pedir = () => {
		setEnlace(null);
		setError(null);
		http.enrollmentRequestsService.capturaCrear(campusId, tipo)
			.then((r) => setEnlace(r.data))
			.catch(() => setError("No se pudo crear el código. Intenta de nuevo."));
	};
	useEffect(pedir, [campusId, tipo]);

	/** Baja la foto si ya llegó; true = la tomó. */
	const tomar = async (token: string): Promise<boolean> => {
		if (listo.current) return true;
		const r = await http.enrollmentRequestsService.capturaTomar(campusId, token);
		if (r.status === 204 || !r.data || (r.data as Blob).size === 0) return false;
		listo.current = true;
		const blob = r.data as Blob;
		const nombre = /filename="([^"]+)"/.exec(String(r.headers?.["content-disposition"] || ""))?.[1] || tipo + "-celular.jpg";
		onFoto(new File([blob], nombre, { type: blob.type || "image/jpeg" }));
		onClose();
		return true;
	};

	useEffect(() => {
		if (!enlace) return;
		const soltar = push.usar(campusId);
		const quitar = push.on("captura", (d: any) => {
			if (d?.token !== enlace.token) return;
			setBajando(true);
			tomar(enlace.token).catch(() => setError("Llegó la foto pero no se pudo bajar. Pide otro código.")).finally(() => setBajando(false));
		});
		// Respaldo sin aviso al instante: solo mientras este recuadro está abierto.
		const t = setInterval(() => {
			setAhora(Date.now());
			if (!push.conectado && !listo.current) tomar(enlace.token).catch(() => { /* se reintenta */ });
		}, 4000);
		return () => { quitar(); soltar(); clearInterval(t); };
	}, [enlace]);

	const svg = useMemo(() => {
		if (!enlace) return "";
		const q = qrcode(0, "M");
		q.addData(enlace.url);
		q.make();
		return q.createSvgTag({ cellSize: 6, margin: 2, scalable: true });
	}, [enlace]);

	const vencido = !!enlace && new Date(enlace.vence).getTime() < ahora;
	const minutos = enlace ? Math.max(0, Math.ceil((new Date(enlace.vence).getTime() - ahora) / 60000)) : 0;

	return (
		<Modal show onHide={onClose} centered className="waModulo">
			<Modal.Header closeButton>
				<Modal.Title style={{ fontSize: 18 }}><FontAwesomeIcon icon={solid("mobile-screen")} />&nbsp;Tomar la foto con el celular</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "center" }}>
				{error ? <div className="waAviso" style={{ textAlign: "left" }}>{error}</div> : null}
				{!enlace && !error ? <div style={{ padding: 40, color: "var(--wa-apagado)" }}>Creando el código…</div> : null}
				{enlace && !vencido ? (
					<>
						<div style={{ fontWeight: 600, marginBottom: 6 }}>{enlace.titulo}</div>
						<div style={{ width: 220, height: 220, margin: "0 auto" }} dangerouslySetInnerHTML={{ __html: svg }} />
						<ol style={{ textAlign: "left", fontSize: 14, margin: "12px auto 4px", maxWidth: 320, paddingLeft: 20 }}>
							<li>Abre la cámara de tu celular y apunta al código.</li>
							<li>Toca el enlace que aparece y toma la foto.</li>
							<li>Aquí se agrega sola en unos segundos.</li>
						</ol>
						<div style={{ fontSize: 13, color: "var(--wa-apagado)" }}>
							{bajando ? "Llegó la foto, agregándola…" : "Esperando la foto · el código sirve " + minutos + (minutos === 1 ? " minuto más" : " minutos más")}
						</div>
					</>
				) : null}
				{vencido ? (
					<div style={{ padding: 20 }}>
						<div style={{ marginBottom: 12 }}>El código venció.</div>
						<button type="button" className="btn btn-primary" onClick={pedir}>Crear otro código</button>
					</div>
				) : null}
			</Modal.Body>
		</Modal>
	);
};

export default FotoCelular;
